<template>
  <Analytics />
  <AppLoader />
  <div class="d-d-flex">
    <div class="d-d-grid d-gl-header d-h100p dp3-wrapper d-fl1 d-ps-relative">
      <TopBar />
      <router-view />
      <aside class="d-toast-wrapper dp3-toast-wrapper">
        <dt-toast
          v-model:show="toastState.show"
          :message="toastState.message"
          :kind="toastState.kind"
          :important="toastState.important"
          :hide-close="toastState.hideClose"
          :duration="toastState.duration"
        >
          <template #icon>
            <component v-if="toastState.icon" :is="toastState.icon" size="400" />
            <dt-icon-check v-else size="400" />
          </template>
          <template #action>
            <dt-button
              v-if="toastState.actionLabel && toastState.action"
              importance="clear"
              kind="muted"
              @click="
                () => {
                  if (toastState.action) toastState.action();
                  toastState.show = false;
                }
              "
            >
              {{ toastState.actionLabel }}
            </dt-button>
          </template>
        </dt-toast>
      </aside>
    </div>
    <SidePanel />
    <div
      v-if="isDragging"
      class="dp3-global-drop-zone d-ps-absolute d-d-flex d-fd-column d-ai-center d-jc-center d-zi-max"
      :class="{
        'dp3-global-drop-zone--expanded': isDragHovering,
        'dp3-global-drop-zone--partial': isPartiallyOpen && !showSidePanel,
      }"
      @dragover="onDropZoneDragOver"
      @dragenter="onDropZoneDragEnter"
      @dragleave="onDropZoneDragLeave"
      @drop="onDropZoneDrop"
    >
      <!-- Global Drop Zone Overlay (shown when dragging) -->
    </div>
  </div>
</template>

<script setup lang="ts">
import TopBar from '@/components/TopBar.vue';
import SidePanel from '@/components/SidePanel.vue';
import { useGlobalToast, toastState } from '@/composables/ui/useGlobalToast';
import { useUserStore } from '@/store/useUserStore';
import { useDataStore } from '@/store/useDataStore';
import { onMounted, nextTick, ref, onUnmounted } from 'vue';
import { DtToast, DtButton } from '@dialpad/dialtone/vue3';
import { DtIconCheck } from '@dialpad/dialtone-icons/vue3';
import AppLoader from '@/components/AppLoader.vue';
import { useSidePanel } from '@/composables/ui/useSidePanel';
import { useGlobalDragDrop } from '@/composables/drag-drop/useGlobalDragDrop';
import { Analytics } from '@vercel/analytics/vue';

const { showSidePanel, isDragHovering, isPartiallyOpen } = useSidePanel();

const {
  isDragging,
  onDropZoneDragOver,
  onDropZoneDragEnter,
  onDropZoneDragLeave,
  onDropZoneDrop,
  setupGlobalDragListeners,
  removeGlobalDragListeners,
} = useGlobalDragDrop();

// Keep console logs behind a debug flag
import { useDebugLogger } from '@/utils/debugLogger';

// Initialize stores to ensure data is properly loaded
const userStore = useUserStore();
const dataStore = useDataStore();

// Debug logger
const debugLogger = useDebugLogger();

onMounted(async () => {
  // Initialize user store first and wait for it to be ready
  userStore.initialize();

  // Wait for user store to be initialized
  await nextTick();

  // Force a refresh of the data store to ensure all components update
  // This is needed because some components might not properly react to user ID changes
  dataStore.$reset();
  dataStore.$patch({});

  debugLogger.log(
    'App initialization complete. UserStore initialized:',
    userStore.initialized,
    'User ID:',
    userStore.userId
  );
});

// Initialize composables
useGlobalToast();

onMounted(async () => {
  // Initialize user store first and wait for it to be ready
  userStore.initialize();

  // Wait for user store to be initialized
  await nextTick();

  // Force a refresh of the data store to ensure all components update
  dataStore.$reset();
  dataStore.$patch({});

  // Setup global drag listeners
  setupGlobalDragListeners();
});

onUnmounted(() => {
  // Cleanup listeners
  removeGlobalDragListeners();
});
</script>
